import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { uploadNonFinancialTransactions, triggerNonFinancialProcessing, getNonFinancialUploadRecords } from "../../service/query/auth";
import { LoadingIcon } from "../../assets/icons";

const statusStyles = {
    PENDING: "bg-[#F3F4F6] text-[#6B7280]",
    PROCESSING: "bg-[#EDF6FF] text-[#0057FF]",
    SUCCESSFUL: "bg-[#E7F7EF] text-[#0E9F6E]",
    FAILED: "bg-[#FDECEC] text-[#E02424]",
};

const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1 px-[10px] py-[3px] rounded-full text-[12px] font-medium ${statusStyles[status] ?? "bg-[#F3F4F6] text-[#6B7280]"}`}>
        <span className="w-[6px] h-[6px] rounded-full bg-current" />
        {status}
    </span>
);

export default function PointsAwardUpload() {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [result, setResult] = useState(null);
    const [uploadId, setUploadId] = useState(null);
    const [records, setRecords] = useState([]);
    const [triggered, setTriggered] = useState(false);
    const [error, setError] = useState(null);

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError(null);
        setResult(null);
        setRecords([]);
        setTriggered(false);
        setUploadId(null);
        try {
            const res = await uploadNonFinancialTransactions(file);
            const data = res?.data;
            console.log("Upload response:", data);
            setResult(data);

            if (data?.upload_id) {
                setUploadId(data.upload_id);

                try {
                    const recordsRes = await getNonFinancialUploadRecords(data.upload_id);
                    const recordsData = recordsRes?.data;
                    setRecords(recordsData?.data ?? []);

                    if (recordsData?.processing_trigger === true) {
                        setTriggered(true);
                    }
                } catch (err) {
                    console.error("Failed to fetch records:", err);
                }
            }
        } catch (err) {
            const errData = err?.response?.data;

            if (errData?.message && typeof errData.message === "object") {
                const firstKey = Object.keys(errData.message)[0];
                const firstError = errData.message[firstKey];
                setError(Array.isArray(firstError) ? firstError[0] : String(firstError));
            } else if (typeof errData?.message === "string") {
                setError(errData.message);
            } else {
                setError("Upload failed. Please try again.");
            }
        } finally {
            setUploading(false);
        }
    };

    const handleTrigger = async () => {
        if (!uploadId) return;
        setTriggering(true);
        try {
            await triggerNonFinancialProcessing(uploadId);
            setTriggered(true);
        } catch (err) {
            const msg = err?.response?.data?.message ?? "Trigger failed. Please try again.";
            setError(msg);
        } finally {
            setTriggering(false);
        }
    };

    const handleDownloadTemplate = () => {
        const headers = ["points", "amount", "transaction_date", "membership_id", "bill_no", "reason"];
        const sampleRow = ["1000", "500", "2026-07-15 10:00:00", "8711130", "SAMPLEBILL001", "Sign-up Bonus"];
        const csv = `${headers.join(",")}\n${sampleRow.join(",")}`;
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "points-award-template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="bg-white pt-8">
            <div className="flex flex-row justify-between mb-8">
                <div>
                    <h3 className="text-[24px] font-semibold mb-2">Upload Points Award file</h3>
                    <p className="text-[#667085] mb-8">
                        Download the template below, enter the information for each column.
                    </p>

                    <div className="border rounded-xl p-6 flex justify-between items-center mb-10">
                        <div>
                            <h4 className="font-semibold">Import template</h4>
                            <p className="text-sm text-gray-500">
                                CSV file with required columns: points, amount, transaction_date, membership_id, bill_no, reason
                            </p>
                        </div>
                        <button onClick={handleDownloadTemplate} className="border rounded px-4 py-2 ml-10">
                            Download
                        </button>
                    </div>
                </div>
                <div>
                    <button
                        onClick={() => navigate("/points-award/transactions")}
                        className="bg-[#00305E] text-white rounded px-10 py-3"
                    >
                        Close
                    </button>
                </div>
            </div>

            <hr className="mb-8" />

            <h4 className="font-semibold mb-2">Upload</h4>
            <p className="text-gray-500 mb-5">
                Please follow the instructions above to fill in your data correctly and upload. Note: if any row is invalid or duplicated, the entire file will be rejected.
            </p>

            <label className="border-2 border-dashed border-[#BFC8D4] rounded-xl h-[110px] flex justify-center items-center cursor-pointer bg-[#EDF6FF]">
                <span className="text-[#0057FF]">
                    {file?.name ?? "Click here to upload your file"}
                </span>
                <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                        setFile(e.target.files[0]);
                        setResult(null);
                        setRecords([]);
                        setError(null);
                        setTriggered(false);
                        setUploadId(null);
                    }}
                />
            </label>

            <p className="text-sm text-gray-500 mt-3">
                Supported formats: CSV, XLSX, XLS; max file size: 10MB; max rows: 1000.
            </p>

            {file && !result && (
                <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className={`mt-6 rounded px-8 py-2 text-white text-[14px] flex items-center justify-center w-[160px] ${uploading ? "bg-gray-300 cursor-not-allowed" : "bg-[#00305E] hover:bg-blue-800"}`}
                >
                    {uploading ? <LoadingIcon /> : "Upload"}
                </button>
            )}

            {error && (
                <p className="mt-4 text-[#E02424] text-sm">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                </p>
            )}

            {/* Upload summary */}
            {result && (
                <div className="mt-8 border rounded-xl p-6 max-w-[600px]">
                    <h4 className="font-semibold mb-4">Upload result</h4>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Total rows</span>
                            <span className="font-medium">{result.total_rows}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Stored</span>
                            <span className="font-medium text-[#0E9F6E]">{result.stored_count}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Rejected</span>
                            <span className="font-medium text-[#E02424]">{result.rejected_count}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Duplicates</span>
                            <span className="font-medium">{result.duplicate_count}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Already processed</span>
                            <span className="font-medium">{result.already_processed_count}</span>
                        </div>
                    </div>

                    {result.rejected_count > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-medium mb-2 text-[#E02424]">Rejected rows</p>
                            <ul className="text-sm text-[#E02424] space-y-1 max-h-[150px] overflow-y-auto">
                                {result.rejected.map((r, i) => (
                                    <li key={i}>Row {r.row}: {r.reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {uploadId && !triggered && result.stored_count > 0 && (
                        <button
                            onClick={handleTrigger}
                            disabled={triggering}
                            className={`mt-6 rounded px-6 py-2 text-white text-sm flex items-center justify-center w-[180px] ${triggering ? "bg-gray-300 cursor-not-allowed" : "bg-[#00305E] hover:bg-blue-800"}`}
                        >
                            {triggering ? <LoadingIcon /> : "Trigger Processing"}
                        </button>
                    )}

                    {triggered && (
                        <p className="mt-4 text-[#0E9F6E] text-sm font-medium">
                            {/* ✅ Processing triggered successfully! */}
                            Processing triggered successfully!
                        </p>
                    )}
                </div>
            )}

            {/* Records table */}
            {records.length > 0 && (
                <div className="mt-8">
                    <h4 className="font-semibold mb-4">Uploaded records</h4>
                    <div className="rounded-md overflow-hidden border border-[#E9EAEB]">
                        <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
                            <table className="w-full text-sm text-left min-w-[1200px]">
                                <thead className="bg-[#F8FAFC] sticky top-0">
                                    <tr>
                                        {["Bill No", "Membership ID", "Points", "Amount", "Transaction Date", "Reason", "Status"].map((h) => (
                                            <th key={h} className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((row) => (
                                        <tr key={row.id} className="border-b hover:bg-blue-50 transition-colors">
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.bill_no ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.membership_id}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.points}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.amount}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.transaction_date}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.reason ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[14px] whitespace-nowrap">
                                                <StatusBadge status={row.processing_status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate("/points-award/transactions")}
                        className="mt-6 bg-[#00305E] text-white rounded px-6 py-2 text-sm"
                    >
                        View all transactions
                    </button>
                </div>
            )}
        </div>
    );
}